import { createFileRoute, Link } from "@tanstack/react-router";
import Navbar from "@/components/Navbar";
import CityHeroShared from "@/components/CityHeroShared";

import VehicleTypeIcons from "@/components/VehicleTypeIcons";
import SeoLongform from "@/components/SeoLongform";
import GeoAnswerBlock from "@/components/GeoAnswerBlock";
import { MapPin, ShieldCheck, Users, Star, Phone, Sparkles, Church, GraduationCap, PlaneTakeoff, ArrowRight } from "lucide-react";
import laImg from "@/assets/home-la.webp";
import orlandoImg from "@/assets/home-orlando.webp";
import vegasImg from "@/assets/home-vegas.webp";
import phoenixImg from "@/assets/home-phoenix.webp";
import sandiegoImg from "@/assets/home-sandiego.webp";
import newyorkImg from "@/assets/home-newyork.webp";
import atlantaImg from "@/assets/home-atlanta.webp";
import ocImg from "@/assets/home-orangecounty.webp";
import miamiImg from "@/assets/home-miami.webp";
import dcImg from "@/assets/home-dc.webp";
import minneapolisImg from "@/assets/home-minneapolis.webp";
import nashvilleImg from "@/assets/landmark-nashville.jpg";
import chicagoImg from "@/assets/landmark-chicago.jpg";
import sanfranciscoImg from "@/assets/landmark-sanfrancisco.jpg";

const ACCENT = "#F97316";

const FAQS = [
  {
    q: "How much does it cost to rent a passenger van?",
    a: "Sprinter and 15-passenger van rentals start around $195/day, with weekly and monthly discounts. Live pricing shows the moment you enter your city and dates.",
  },
  {
    q: "Do you rent 12 and 15 passenger vans?",
    a: "Yes — we specialize in 12 and 15 passenger vans including Mercedes Sprinters, Ford Transits, and premium minivans for groups, churches, schools, and airport shuttles.",
  },
  {
    q: "Which cities do you serve?",
    a: "We operate our own Sprinter fleets in every major U.S. metro — Orlando, Miami, Los Angeles, Orange County, San Diego, San Francisco, Las Vegas, Phoenix, Atlanta, Nashville, Chicago, Washington DC, New York, and Minneapolis — and can arrange nationwide van rentals in every major U.S. city.",
  },
  {
    q: "Do you offer church and school van rentals?",
    a: "Yes. We have dedicated church van rental and school van rental programs with tax-exempt billing, insurance requirements handled, and volume pricing for repeat trips.",
  },
  {
    q: "Is there a young driver fee or minimum age?",
    a: "Primary drivers must be 21 or older. Additional drivers can be added at checkout with a valid license and insurance.",
  },
];

const LOCATIONS = [
  { to: "/orlando" as const, name: "Orlando, FL", img: orlandoImg },
  { to: "/miami" as const, name: "Miami, FL", img: miamiImg },
  { to: "/losangeles" as const, name: "Los Angeles, CA", img: laImg },
  { to: "/orangecounty" as const, name: "Orange County, CA", img: ocImg },
  { to: "/san-diego" as const, name: "San Diego, CA", img: sandiegoImg },
  { to: "/sanfrancisco" as const, name: "San Francisco, CA", img: sanfranciscoImg },
  { to: "/vegas" as const, name: "Las Vegas, NV", img: vegasImg },
  { to: "/phoenix" as const, name: "Phoenix, AZ", img: phoenixImg },
  { to: "/atlanta" as const, name: "Atlanta, GA", img: atlantaImg },
  { to: "/nashville" as const, name: "Nashville, TN", img: nashvilleImg },
  { to: "/chicago" as const, name: "Chicago, IL", img: chicagoImg },
  { to: "/washingtondc" as const, name: "Washington, DC", img: dcImg },
  { to: "/newyork" as const, name: "New York, NY", img: newyorkImg },
  { to: "/minneapolis" as const, name: "Minneapolis, MN", img: minneapolisImg },
];

export const Route = createFileRoute("/")({
  component: HomePage,
  head: () => ({
    meta: [
      { title: "12 & 15 Passenger Van Rentals Nationwide | Sprinter Van Rentals" },
      { name: "description", content: "Rent a 12 or 15 passenger van nationwide. Mercedes Sprinter, Ford Transit and premium minivan rentals with airport delivery, church & school programs, weddings and road trips. Live pricing in every major U.S. city." },
      { name: "keywords", content: "12 passenger van rental, 15 passenger van rental, passenger van rental, Mercedes Sprinter rental, Ford Transit rental, church van rental, school van rental" },
      { property: "og:title", content: "12 & 15 Passenger Van Rentals Nationwide | Sprinter Van Rentals" },
      { property: "og:description", content: "Book a 12 or 15 passenger van in any major U.S. city. Live pricing, unlimited mileage options, church & school programs." },
      { property: "og:url", content: "https://sprinter-van-rentals.com/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://sprinter-van-rentals.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function HomePage() {
  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* HERO ---------------------------------------------------------- */}
      <CityHeroShared
        headline={"12 & 15 PASSENGER SPRINTER\nVAN RENTALS — NEAR YOU"}
        intro="Book a Mercedes Sprinter, Ford Transit or premium minivan in any major U.S. city. Live pricing, airport pickup, and dedicated church & school van rental programs."
      />


      {/* TRUST BAR ----------------------------------------------------- */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: Users, label: "50,000+ Passengers Moved" },
            { icon: Star, label: "4.9★ Average Rating" },
            { icon: ShieldCheck, label: "Fully Insured Fleet" },
            { icon: PlaneTakeoff, label: "Airport Pickup Included" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <Icon size={22} style={{ color: ACCENT }} />
              <span className="text-[13px] font-display font-bold uppercase tracking-[0.18em] text-neutral-800">
                {label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* PICK YOUR VAN ------------------------------------------------- */}
      <section id="fleet" className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-10">
          <p className="font-display font-bold text-sm uppercase tracking-[0.35em] mb-3" style={{ color: ACCENT }}>
            The Fleet
          </p>
          <h2 className="font-display font-extrabold text-[3.25rem] md:text-5xl tracking-tight text-neutral-900 leading-tight">
            Pick&nbsp; Your Sprinter. Book It.
          </h2>
        </div>
        <VehicleTypeIcons accent={ACCENT} />
      </section>

      {/* SEO CONTENT --------------------------------------------------- */}
      <section className="bg-neutral-950 text-neutral-100">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-white">
              Sprinter Van Rentals Built for Real Trips.
            </h2>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              Sprinter Van Rentals is the specialist passenger van rental company for
              groups that need <strong>12 and 15 passenger vans</strong> without the
              rental-counter chaos. Every Sprinter, Transit and minivan in our fleet
              is late-model, fully insured, and delivered ready for your trip — with
              live pricing that never changes at pickup.
            </p>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              Whether you need an <strong>airport pickup for a bachelorette party</strong>,
              a <strong>Sunday church van</strong>, a <strong>school field trip shuttle</strong>,
              or a one-way road trip across three states, we set up the whole trip in a
              few clicks.
            </p>
          </div>
          <div className="grid gap-4">
            {[
              {
                icon: Church,
                title: "Church Van Rentals",
                body: "Recurring Sunday rentals, retreats, and youth trips with tax-exempt billing and volume pricing.",
              },
              {
                icon: GraduationCap,
                title: "School Van Rentals",
                body: "Field trips, athletics, and university group travel. Insurance certificates and PO billing handled.",
              },
              {
                icon: PlaneTakeoff,
                title: "Airport Pickup",
                body: "Meet-and-greet Sprinter service at every major U.S. airport we serve, or self-pickup near the terminal.",
              },
              {
                icon: Star,
                title: "Weddings & Events",
                body: "Executive Sprinters with leather captain's chairs for wedding party shuttles and premium transfers.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center gap-3">
                  <Icon size={20} style={{ color: ACCENT }} />
                  <h3 className="font-display font-bold text-lg text-white">{title}</h3>
                </div>
                <p className="mt-2 text-sm text-neutral-300">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATIONS (compact) ------------------------------------------ */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <p className="font-display font-bold text-xs uppercase tracking-[0.35em]" style={{ color: ACCENT }}>
            Fleet Cities
          </p>
          <h2 className="mt-2 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-neutral-900">
            Rent a Van In Your City.
          </h2>
          <p className="mt-3 text-neutral-600 max-w-2xl mx-auto">
            Each metro runs its own Sprinter fleet with local pricing and airport pickup.
            Don't see your city? Search any U.S. metro in the booking widget above.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {LOCATIONS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="group relative rounded-xl overflow-hidden h-24 md:h-28 border border-neutral-200 hover:border-neutral-900 transition"
            >
              <img
                src={l.img}
                alt={`Van rentals in ${l.name}`}
                width={320}
                height={112}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="relative z-10 h-full flex items-end p-3">
                <div className="flex items-center gap-1.5 text-white">
                  <MapPin size={12} style={{ color: ACCENT }} />
                  <span className="font-display font-bold text-sm leading-tight drop-shadow">{l.name}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ ----------------------------------------------------------- */}
      <section className="bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <p className="font-display font-bold text-xs uppercase tracking-[0.35em] text-center" style={{ color: ACCENT }}>
            FAQ
          </p>
          <h2 className="mt-2 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-neutral-900 text-center">
            Van Rental Questions.
          </h2>
          <div className="mt-10 space-y-4">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-xl border border-neutral-200 bg-white p-5 open:shadow-md transition">
                <summary className="cursor-pointer font-display font-bold text-lg text-neutral-900 flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-2xl leading-none transition group-open:rotate-45" style={{ color: ACCENT }}>+</span>
                </summary>
                <p className="mt-3 text-neutral-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <SeoLongform
        title="Nationwide 12 & 15 passenger van rentals"
        paragraphs={[
          "Sprinter Van Rentals is a nationwide 12 and 15 passenger van rental company that operates its own Sprinter and Ford Transit fleets in every major U.S. metro area and can arrange passenger van rentals in every major city across the country. If you have ever tried to rent a 15 passenger van from a traditional rental car agency you already know the story: limited availability, surprise young driver fees, mandatory upsells at the counter, and vans that may or may not actually be on the lot when you arrive. We built Sprinter Van Rentals to fix that experience for churches, schools, weddings, corporate teams, sports teams, and family groups who just need a reliable van, a fair price, and a real person to talk to.",
          "Our national 12 and 15 passenger van rental fleet is anchored by three vehicle families. The Ford Transit low roof 12 passenger van rental is our most affordable option and fits in standard parking garages and residential driveways. The medium roof Ford Transit 15 passenger van rental adds interior headroom and cargo space for airport runs and longer road trips. The Mercedes Sprinter 15 passenger van rental is our premium option, with high roof interiors, upgraded seating, and full-size luggage capacity behind the last row. We also stock premium Sprinter Executive vans for VIP transportation and corporate work, and minivans for smaller groups that still need more room than a sedan.",
          "We deliver 12 and 15 passenger vans to every major U.S. airport, including free curbside meet and greet at MCO in Orlando, MIA in Miami, LAX in Los Angeles, JFK and LGA in New York, LAS in Las Vegas, PHX in Phoenix, ATL in Atlanta, and every other airport in every city we serve. Delivery to hotels, event venues, wedding venues, corporate offices, university campuses, and residential addresses is also available in every service area.",
          "Renting a 12 or 15 passenger van from Sprinter Van Rentals is designed to take less than two minutes. Enter your city and dates, choose your van, review the live rate before you enter payment, and lock in your reservation with a refundable deposit. Add optional extras like additional drivers, child seats, and airport delivery in one click, and you are done. Our reservation team is available seven days a week to answer questions about vehicle sizing, insurance, young driver rules, long-term rentals, one-way rentals, and every other detail about passenger van rentals in the U.S.",
        ]}
      />
      <GeoAnswerBlock />

      <footer className="py-8 text-center border-t border-border">
        <p className="text-muted-foreground text-xs tracking-widest uppercase">
          &copy; 2026 Sprinter Van Rentals. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
